import { PropsWithChildren, useEffect, useMemo, useRef } from 'react';
import { isEmpty } from 'lodash-es';
import { useDevToolsStore } from '@frontend/devtools';
import { PX_PER_MINUTE } from '../constants';
import {
  CalendarGridStore,
  CalendarGridStoreProvider,
  ColumnsById,
  useNonReactiveStore,
} from '../store/calendar-grid.store';
import { ZoomStore, ZoomStoreProvider, useNonReactiveZoomStore } from '../store/zoom.store';
import {
  AllDayCalendarEvent,
  CalendarColumn,
  CalendarColumnGroup,
  CalendarColumnHeaderType,
  CalendarEvent,
  CalendarGridDragStyles,
  CalendarGridRenderProp,
} from '../types';
import { convertToIdMap } from '../utils';
import { useZoomKeyboardShortcuts } from '../zoom/use-zoom-keyboard-shortcuts';
import type { ZoomConfig } from '../zoom/zoom-types';

type BaseCalendarGridProviderProps = {
  calendarDate?: string;
  events: CalendarEvent[];
  allDayEvents?: AllDayCalendarEvent[];
  /**
   * Optional column groups for organizing columns.
   * If not provided, a default group containing all columns will be created automatically.
   */
  columnGroups?: CalendarColumnGroup[];
  columnBodyVariant?: CalendarGridStore['columnBodyVariant'];
  pxPerMinute?: number;
  timeIndicatorLines?: CalendarGridStore['timeIndicatorLines'];
  baseTimezone?: string;
  onEventsSelect?: CalendarGridStore['onEventsSelect'];
  onColumnReorder?: (groupId: string, newColumnIds: string[]) => void;
  isColumnReorderActive?: boolean;
  /** Called when a column header (provider/operatory/etc.) is clicked, with the column id and the group id it belongs to. */
  onColumnHeaderClick?: (columnId: string, groupId: string) => void;
  onEventDragStart?: (eventId: string) => void;
  onEventDragEnd?: (
    eventId: string,
    targetColumnId: string,
    targetGroupId: string,
    newStartMinutes: number,
    clearPreview: () => void
  ) => void;
  isEventDraggable?: (event: CalendarEvent) => boolean;
  renderEventCard?: CalendarGridRenderProp;
  renderEventTooltip?: CalendarGridRenderProp;
  dragSnapMinutes?: number;
  enableDroppableColumns?: boolean;
  dragStyles?: CalendarGridDragStyles;
  zoomConfig?: ZoomConfig;
  shouldShowStripeBackground?: boolean;
  /** The event ID that was arrived at via an external deep-link. When set, the matching event card
   *  (or overflow button covering it) will be highlighted and scrolled into view. */
  deepLinkedEventId?: string;
  selectedEventIds?: string[];
};

type CalendarGridProviderPropsByHeaderType<THeader extends CalendarColumnHeaderType> = BaseCalendarGridProviderProps & {
  headerType: THeader;
  columns: CalendarColumn<THeader>[];
};

export type CalendarGridProviderProps = {
  [THeader in CalendarColumnHeaderType]: CalendarGridProviderPropsByHeaderType<THeader>;
}[CalendarColumnHeaderType];

export const CalendarGridProvider = ({
  children,
  calendarDate,
  headerType,
  columnBodyVariant = 'grid',
  events,
  allDayEvents = [],
  columns,
  columnGroups,
  pxPerMinute = PX_PER_MINUTE,
  timeIndicatorLines = 'hourly',
  baseTimezone,
  onEventsSelect,
  onColumnReorder,
  isColumnReorderActive = false,
  onColumnHeaderClick,
  onEventDragStart,
  onEventDragEnd,
  isEventDraggable,
  renderEventCard,
  renderEventTooltip,
  dragSnapMinutes = 5,
  enableDroppableColumns = false,
  dragStyles = {},
  zoomConfig,
  deepLinkedEventId,
  selectedEventIds,
  shouldShowStripeBackground,
}: PropsWithChildren<CalendarGridProviderProps>) => {
  const debug = useDevToolsStore((state) => state.options.isDebugModeOn);

  const onColumnReorderRef = useRef(onColumnReorder);
  onColumnReorderRef.current = onColumnReorder;
  const onEventDragStartRef = useRef(onEventDragStart);
  onEventDragStartRef.current = onEventDragStart;
  const onEventDragEndRef = useRef(onEventDragEnd);
  onEventDragEndRef.current = onEventDragEnd;
  const isEventDraggableRef = useRef(isEventDraggable);
  isEventDraggableRef.current = isEventDraggable;
  const renderEventCardRef = useRef(renderEventCard);
  renderEventCardRef.current = renderEventCard;
  const renderEventTooltipRef = useRef(renderEventTooltip);
  renderEventTooltipRef.current = renderEventTooltip;

  const initialState = useMemo<Partial<CalendarGridStore>>(() => {
    let gridColumnGroups = columnGroups;
    if (!columnGroups?.length) {
      gridColumnGroups = [
        {
          id: columns.length > 0 ? columns[0].groupId : 'default-group',
          name: '',
          columnIds: columns.map((column) => column.id),
          groupOffset: 0,
          timezone: baseTimezone,
          emptyStateVariant: columns.length === 0 ? 'no-columns' : events.length === 0 ? 'no-events' : undefined,
        },
      ];
    }

    const groupIds = gridColumnGroups?.map((group) => group.id) || [];
    const eventsById = convertToIdMap(events);
    const columnsById = columns.reduce((acc, column) => {
      acc[column.id] = { ...acc[column.id], [column.groupId]: column };
      return acc;
    }, {} as ColumnsById);
    const groupsById = convertToIdMap(gridColumnGroups || []);

    return {
      debug,
      calendarDate,
      headerType,
      groupIds,
      groupsById,
      columnsById,
      eventsById,
      allDayEvents,
      hasAllDayEvents: allDayEvents.length > 0,
      timeIndicatorLines: headerType === 'weekday' ? 'none' : timeIndicatorLines,
      hasMultipleGroups: groupIds.length > 1,
      columnBodyVariant,
      isColumnReorderEnabled: !!onColumnReorder,
      isColumnReorderActive,
      onColumnReorder: onColumnReorder,
      isColumnHeaderClickEnabled: !!onColumnHeaderClick,
      onColumnHeaderClick,
      onEventsSelect,
      onEventDragStartRef,
      onEventDragEndRef,
      isEventDraggableRef,
      renderEventCardRef,
      renderEventTooltipRef,
      isEventDragEnabled: !!onEventDragEnd,
      dragSnapMinutes,
      enableDroppableColumns: !!onEventDragEnd || enableDroppableColumns,
      dragStyles,
      deepLinkedEventId,
      selectedEventIds,
      shouldShowStripeBackground,
    } satisfies Partial<CalendarGridStore>;
  }, [
    calendarDate,
    columnGroups,
    events,
    columns,
    debug,
    headerType,
    allDayEvents,
    timeIndicatorLines,
    columnBodyVariant,
    onColumnReorder,
    onColumnHeaderClick,
    onEventsSelect,
    isColumnReorderActive,
    baseTimezone,
    onEventDragEnd,
    dragSnapMinutes,
    enableDroppableColumns,
    dragStyles,
    deepLinkedEventId,
    selectedEventIds,
    shouldShowStripeBackground,
  ]);

  const zoomInitialState = useMemo<Partial<ZoomStore>>(() => {
    if (isEmpty(zoomConfig)) return { pxPerMinute };

    const zoomLevel = zoomConfig.zoomLevel ?? zoomConfig.defaultLevel ?? 1;
    const zoomLevels = zoomConfig.levels ? [...zoomConfig.levels].sort((a, b) => a - b) : [1, 2];
    return {
      zoomEnabled: true,
      showControls: zoomConfig.showControls ?? true,
      zoomLevel,
      zoomLevels,
      pxPerMinute: Math.round(PX_PER_MINUTE * zoomLevel),
      canZoomIn: zoomLevel > zoomLevels[0],
      canZoomOut: zoomLevel < zoomLevels[zoomLevels.length - 1],
      onZoomLevelChange: zoomConfig.onZoomLevelChange,
    } satisfies Partial<ZoomStore>;
  }, [pxPerMinute, zoomConfig]);

  return (
    <CalendarGridStoreProvider initialState={initialState}>
      {/* // TODO: this is a workaround to sync the initial state to the store. We should ideally not need this
         // and directly use the initialState in the provider, but that requires some changes
        // in our createProvider util to support dynamic initial state. */}
      <CalendarGridStoreSync state={initialState} />
      <ZoomStoreProvider initialState={zoomInitialState}>
        <ZoomStoreSync state={zoomInitialState} />
        {!!zoomConfig?.showControls && <ZoomKeyboardShortcutsActivator />}
        {children}
      </ZoomStoreProvider>
    </CalendarGridStoreProvider>
  );
};

const ZoomKeyboardShortcutsActivator = () => {
  useZoomKeyboardShortcuts();
  return null;
};

const CalendarGridStoreSync = ({ state }: { state: Partial<CalendarGridStore> }) => {
  const store = useNonReactiveStore();

  useEffect(() => {
    store.setState(state);
  }, [state, store]);

  return null;
};

const ZoomStoreSync = ({ state }: { state: Partial<ZoomStore> }) => {
  const store = useNonReactiveZoomStore();

  useEffect(() => {
    store.setState(state);
  }, [state, store]);

  return null;
};
